import React from 'react';
import { AssessmentUserApiResponse } from '../Interfaces';
import { TableColumn } from '../Interfaces/TableColumn';

// Calculate skill percentage
const calculateSkillPercentage = (skill: any): number => {
  if (!skill || skill.totalCount === 0) return 0;
  return Math.round((skill.correctCount / skill.totalCount) * 100);
};

// Format time from seconds or time string to MM:SS format
const formatTime = (timeValue: number | string | undefined): string => {
  if (!timeValue) return '00:00:00';

  let seconds: number;

  // If it's already a string in format H:MM:SS or MM:SS, parse it
  if (typeof timeValue === 'string') {
    const parts = timeValue.split(':').map(Number);
    if (parts.length === 3) {
      // H:MM:SS format
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // MM:SS format
      seconds = parts[0] * 60 + parts[1];
    } else {
      return '00:00:00';
    }
  } else {
    // It's a number in seconds
    seconds = Math.round(timeValue);
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Get dynamic skill columns based on visible skills
export const getDynamicSkillColumns = (
  visibleSkills: Set<string>,
): TableColumn<AssessmentUserApiResponse>[] => {
  if (!visibleSkills.size) return [];

  return Array.from(visibleSkills).map((skillId) => ({
    key: `skill-${skillId}`,
    label: skillId,
    width: '180px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => {
      const skill = row.skills.find((s) => s.skillName === skillId);
      if (!skill) return <div>N/A</div>;

      return (
        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center gap-1.5'>
            <div className='flex-1 min-w-0'>
              <div className='h-1.5 bg-gray-100 rounded overflow-hidden'>
                <div
                    className='h-full bg-blue-600 rounded transition-all'
                    style={{ width: `${calculateSkillPercentage(skill)}%` }}
                />
              </div>
            </div>
            <span className='text-xs font-medium text-gray-700 whitespace-nowrap'>
              { calculateSkillPercentage(skill) }%
            </span>
          </div>
        </div>
      );
    },
    disableSort: true,
  }));
};

// Get dynamic behavior columns based on visible behaviors
export const getDynamicBehaviorColumns = (
  visibleBehaviors: Set<string>,
): TableColumn<AssessmentUserApiResponse>[] => {
  if (!visibleBehaviors.size) return [];

  return Array.from(visibleBehaviors).map((behavior) => ({
    key: `behavior-${behavior}`,
    label: behavior.charAt(0).toUpperCase() + behavior.slice(1),
    width: '180px',
    align: 'left' as const,
    render: (row: AssessmentUserApiResponse) => {
      // Try exact match first, then lowercase
      const score =
        row.behaviorScores?.[behavior] ??
        row.behaviorScores?.[behavior.toLowerCase()];
      if (score === undefined) return <div>-</div>;

      return (
        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center gap-1.5'>
            <div className='flex-1 min-w-0'>
              <div className='h-1.5 bg-gray-100 rounded overflow-hidden'>
                <div
                    className='h-full bg-blue-600 rounded transition-all'
                    style={{ width: `${score}%` }}
                />
              </div>
            </div>
            <span className='text-xs font-medium text-gray-700 whitespace-nowrap'>
              { score }%
            </span>
          </div>
        </div>
      );
    },
    disableSort: true,
  }));
};
